// Copyright © 2015-2020 TrampleZone LLC All Rights Reserved

// Utilities Module

define('Utilities',['require','MobileUtils','EventUtils','AlertPanel','Sanitizer','Images'],function (require) {
    'use strict';

    var // Require.js module dependencies
        MobileUtils = require('MobileUtils'),
        EventUtils = require('EventUtils'),
        AlertPanel = require('AlertPanel'),
        Sanitizer = require('Sanitizer'),
        Images = require('Images'),

        // Command code constants for server requests
        commandCodes = {
            'pingService':                        '0',
            'newUser':                          '101',
            'getUser':                          '102',
            'updateUser':                       '103',
            'verifyUserEmail':                  '104',
            'verifyUserPhone':                  '105',
            'resetUserPassword':                '106',
            'changeUserPassword':               '107',
            'changeUserEmail':                  '108',
            'resendActivationEmail':            '109',
            'twitterRequest':                   '110',
            'enterScore':                       '201',
            'getScores':                        '202',
            'getScoreComments':                 '203',
            'getYelpRating':                    '204',
            'updateCommentVotes':               '205',
            'getFriends':                       '301',
            'updateFriends':                    '302',
            'referFriend':                      '303',
            'getCheckins':                      '304',
            'getFavoriteLocations':             '305',
            'updateFavoriteLocations':          '306',
            'getUserLocations':                 '307',
            'getMyUserLocations':               '308',
            'updateMyUserLocation':             '309',
            'getFaqs':                          '401',
            'updateFaqs':                       '402',
            'getMotd':                          '501',
            'updateMotd':                       '502',
            'getLeaders':                       '601',
            'getPolicies':                      '701',
            'updatePolicies':                   '702',
            'sendFeedback':                     '801',
            'sendSupport':                      '802',
            'getMerchant':                      '901',
            'updateMerchant':                   '902',
            'getMerchantPromotions':            '903',
            'paymentRequest':                   '1001'
        },

        // Logging support
        loggingEnabled = true,      // True if logging is enabled

        // Current application name, updated by index.html meta tags
        applicationName = '',

        // Current application version, updated by index.html meta tags
        applicationVersion = '',

        // Current application short description, updated by index.html meta tags
        applicationDescription = '',

        // Application ID, dynamically derived from current application name
        applicationId,

        // Default application name
        defaultApplicationName = 'SizzleScene',

        // Default application ID, derived from default application Name
        defaultApplicationId,

        // Email server domain
        emailServerDomain = 'sizzlescene',

        // Company name
        companyName = 'TrampleZone',

        // API version for this client (private interface version for Server)
        apiVersion = '00.80.00.00',

        // Local Storage support confirmation
        localStorageSupported,

        // Unique identifier (paramount to a uuid)
        uuid,
        uuidKey,

        // Welcome message key
        welcomeKey,

        // Mobile
        isMobile,                   // Check for mobile exactly once

        // Dependent Resources
        self,
        mobileUtils,
        eventUtils,
        alertPanel,
        images,

        // Map distance constants and precision
        feetPerMeter = 3.28,
        metersPerMile = 1609.344,
        metersPerTenthMile = 160.9344,
        metersPerKilometer = 1000,
        defaultLatLngPrecision = 8,

        // Message types
        messageTypes = [
            'info',
            'success',
            'warning',
            'error'
        ],

        // Elapsed time marker
        elapsedTime,

        // Performance statistics
        perfStats = {},

        // Default name for place before details are fetched
        defaultPlaceName = 'Loading...',

        // Default displayName if not set
        defaultDisplayName = '** No Name Set **',

        // Default map position
        defaultPosition = {
            latitude: 25.76167980,
            longitude: -80.19179020
        },

        // Accelerometer
        hasAccelerometer = false,

        // Score permission
        canScore,

        // Score error messages
        outOfRangeText = 'Enjoy browsing while you are too far away to score',
        noLocationServicesText = 'Please enable Location Services before submitting scores',

        scoringRange = 8000,  // 5mi / 8km

        // Html sanitizer basic tag policy
        basicTagPolicy,

        // Status bar size
        statusBarSize,

        // Notch sizes, if any for current device
        topNotchSize,
        bottomNotchSize,

        // Merchant website pathname
        merchantWebsitePathname = '/merchants/index.html',

        // Smart quote map to regular quotes
        // Inspired by https://stackoverflow.com/questions/48678359/
        //     ios-11-safari-html-disable-smart-punctuation
        smartQuotesMap = {

            // Open-quotes:
            // http://www.fileformat.info/info/unicode/category/Pi/list.htm
            0x2018: '\'',
            0x201B: '\'',
            0x201C: '"',
            0x201F: '"',

            // Close-quotes:
            // http://www.fileformat.info/info/unicode/category/Pf/list.htm
            0x2019: '\'',
            0x201D: '\"',

            // Primes:
            // http://www.fileformat.info/info/unicode/category/Po/list.htm
            0x2032: '\'',
            0x2033: '"',
            0x2035: '\'',
            0x2036: '"',

            0x2014: '-', // iOS 11 also replaces dashes with em-dash
            0x2013: '-'  // and "--" with en-dash
        };




    // Constructor
    function Utilities () {
        // Preserve instance reference and enforce singleton
        if (typeof self === 'object') { return self; } else { self = this; }

        // Dependent Resources
        mobileUtils = new MobileUtils();
        eventUtils = new EventUtils();
        alertPanel = new AlertPanel('alert-panel');
        images = new Images();

        // Construct a basic html tag policy
        basicTagPolicy = window.html.makeTagPolicy();
    }

    // Public Methods / Variables

    // Retrieve a unique identifier for this device
    // Selected algorithm posted by John Fowler at
    // http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript
    Utilities.prototype.getDeviceId = function () {
        if (typeof uuidKey == 'undefined') {
            uuidKey = 'com.' + applicationName + '.uuid';
        }
        if (!uuid) {
            // First check localStorage
            if (localStorageSupported) {
                uuid = localStorage.getItem(uuidKey);
            }
            // If still not defined, create one
            if (!uuid) {
                var hardware, os, browser;
                // Get Hardware
                if (this.isMobile()) {
                    if (this.isIPad()) {
                        // Tablet
                        hardware = 't';
                    } else {
                        // Phone
                        hardware = 'p';
                    }
                } else {
                    // Desktop
                    hardware = 'd';
                }
                // Get OS
                os = 'u';
                if (this.isMobile()) {
                    if (this.isAndroid()) {
                        // Android
                        os = 'a';
                    } else if (this.isIOS()) {
                        // IOS
                        os = 'i';
                    }
                } else {
                    if (navigator.appVersion) {
                        if (navigator.appVersion.indexOf('Win') != -1) {
                            os = 'w';
                        } else if (navigator.appVersion.indexOf('Mac') != -1) {
                            os = 'm';
                        } else if (navigator.appVersion.indexOf('Linux') != -1) {
                            os = 'l';
                        }
                    }
                }
                // Get browser
                browser = 'u';
                if (this.isMobile()) {
                    if (this.isIOS()) {
                        browser = 's';
                        if (navigator.userAgent.indexOf('CriOS') != -1) {
                            browser = 'c';
                        }
                    } else if (this.isAndroid()) {
                        browser = 'a';
                        if (navigator.userAgent.indexOf('Chrome') != -1) {
                            browser = 'c';
                        }
                    }
                } else if (navigator.userAgent.indexOf('Firefox') != -1) {
                    browser = 'f';
                } else if (navigator.userAgent.indexOf('Chrome') != -1) {
                    browser = 'c';
                } else if (navigator.userAgent.indexOf('Safari') != -1) {
                    browser = 's';
                } else if ((navigator.userAgent.indexOf('Microsoft') != -1) ||
                           (navigator.userAgent.indexOf('Explorer') != -1)) {
                    browser = 'i';
                }
                // Combine to get header
                uuid = hardware + os + browser;
                // Generate uuid and add
                uuid += 'xxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
                    .replace(/[xy]/g, function(c) {
                        var r = Math.random()*16|0, v = c === 'x' ?
                            r : (r&0x3|0x8);
                        return v.toString(16);
                });
                // Store the uuid now that it's calculated
                if (localStorageSupported) {
                    localStorage.setItem(uuidKey, uuid);
                }
            }
        }
        return uuid;
    };

    // Get device from deviceId
    Utilities.prototype.getDeviceFromDeviceId = function () {
        // Get device character from deviceId
        var device,
            deviceChar = this.getDeviceId().charAt(0);
        // Interpret which device to which this maps
        switch (deviceChar) {
            case 't':  device = 'Tablet';       break;
            case 'p':  device = 'Phone';        break;
            case 'd':  device = 'Desktop';      break;
            default:   device = 'Unknown';
        }
        return device;
    };

    // Get OS from deviceId
    Utilities.prototype.getOSFromDeviceId = function (id) {
        // Get os character from deviceId
        var os,
            osChar = this.getDeviceId().charAt(1);
        // Interpret which os to which this maps
        switch (osChar) {
            case 'a':  os = 'Android';          break;
            case 'i':  os = 'IOS';              break;
            case 'w':  os = 'Windows';          break;
            case 'm':  os = 'Mac OS X';         break;
            case 'l':  os = 'Linux';            break;
            default:   os = 'Unknown';
        }
        return os;
    };

    // Get browser from deviceId
    Utilities.prototype.getBrowserFromDeviceId = function (id) {
        // Get browser character from deviceId
        var browser,
            browserChar = this.getDeviceId().charAt(2);
        // Interpret which browser to which this maps
        switch (browserChar) {
            case 's':  browser = 'Safari';      break;
            case 'c':  browser = 'Chrome';      break;
            case 'a':  browser = 'Android';     break;
            case 'f':  browser = 'Firefox';     break;
            case 'i':  browser = 'IExplorer';   break;
            default:   browser = 'Unknown';
        }
        return browser;
    };

    // Quick test to determine if a URL is a dataURI
    Utilities.prototype.isDataURI = function (url) {
        return url && url.length >= 5 && url.substr(0,5) == 'data:';
    };

    // Get random string of specified length
    // Credit: https://stackoverflow.com/questions/1349404/
    //         generate-random-string-characters-in-javascript/8084248#8084248
    Utilities.prototype.getRandomString = function (length) {

        // Allocate a fresh array
        var randomArray = new Uint8Array(parseInt(length / 2));

        // Get crypto object and fill array with random 8-bit integers
        var crypto = window.crypto || window.msCrypto;
        crypto.getRandomValues(randomArray);

        // Convert integers to hex characters
        var randomString = '';
        for (var i=0; i<randomArray.length; i++) {
            randomString += ('0' + randomArray[i].toString(16)).substr(-2);
        }

        // Pad to with 0s to any odd number lengths
        randomString = ('00' + randomString).slice(0 - length);

        // Return random string of specified length
        return randomString;
    };

    // Get Merchant Offer ID (random 27 char string beginning with OID)
    Utilities.prototype.getMerchantOfferId = function () {
        return 'OID-' + this.getRandomString(23);
    };

    // Get Merchant Feedback ID (random 27 char string beginning with FID)
    Utilities.prototype.getFeedbackId = function () {
        return 'FID-' + this.getRandomString(23);
    };

    // Get MOTD ID (random 27 char string beginning with MID)
    Utilities.prototype.getMotdId = function () {
        return 'MID-' + this.getRandomString(23);
    };

    // Get Merchant Subscription ID (random 27 char string beginning with SID)
    Utilities.prototype.getSubscriptionId = function () {
        return 'SID-' + this.getRandomString(23);
    };

    // Get Merchant Discount ID (random 27 char string beginning with DID)
    Utilities.prototype.getDiscountId = function () {
        return 'DID-' + this.getRandomString(23);
    };

    // Get Merchant Credit ID (random 27 char string beginning with CID)
    Utilities.prototype.getCreditId = function () {
        return 'CID-' + this.getRandomString(23);
    };

    // Get Merchant Payment ID (random 27 char string beginning with PID)
    Utilities.prototype.getPaymentId = function () {
        return 'PID-' + this.getRandomString(23);
    };

    // Get TaxRate ID (random 27 char string beginning with TID)
    Utilities.prototype.getTaxJurisdictionId = function () {
        return 'TID-' + this.getRandomString(23);
    };

    // Get Schedule ID (random 27 char string beginning with SCD)
    Utilities.prototype.getScheduleId = function () {
        return 'SCD-' + this.getRandomString(23);
    };

    // Get Vote ID (random 27 char string beginning with VOT)
    Utilities.prototype.getVoteId = function () {
        return 'VOT-' + this.getRandomString(23);
    };

    // Get MOTD ID (random 27 char string beginning with MID)
    Utilities.prototype.getMotdId = function () {
        return 'MID-' + this.getRandomString(23);
    };

    // Get RatingCategory ID (random 27 char string beginning with CAT)
    Utilities.prototype.getRatingCategoryId = function () {
        return 'CAT-' + this.getRandomString(23);
    };

    // Get Content ID (random 27 char string beginning with CON)
    Utilities.prototype.getContentId = function () {
        return 'CON-' + this.getRandomString(23);
    };

    // Return true if the specified string is a merchant offer ID
    Utilities.prototype.isMerchantOfferId = function (id) {
        return id.length == 27 && id.match(/^OID-/);
    };

    // Return true if the specified string is a merchant feedback ID
    Utilities.prototype.isMerchantFeedbackId = function (id) {
        return id.length == 27 && id.match(/^FID-/);
    };

    // Return true if the specified string is a merchant subscription ID
    Utilities.prototype.isSubscriptionId = function (id) {
        return id.length == 27 && id.match(/^SID-/);
    };

    // Return true if the specified string is a merchant credit ID
    Utilities.prototype.isCreditId = function (id) {
        return id.length == 27 && id.match(/^CID-/);
    };

    // Return true if the specified string is a merchant payment ID
    Utilities.prototype.isPaymentId = function (id) {
        return id.length == 27 && id.match(/^PID-/);
    };

    // Return true if the specified string is a tax rate ID
    Utilities.prototype.isTaxJurisdictionId = function (id) {
        return id.length == 27 && id.match(/^TID-/);
    };

    // Return true if the specified string is a schedule ID
    Utilities.prototype.isScheduleId = function (id) {
        return id.length == 27 && id.match(/^SCD-/);
    };

    // Return true if the specified string is a vote ID
    Utilities.prototype.isVoteId = function (id) {
        return id.length == 27 && id.match(/^VOT-/);
    };

    // Determine if an id is an MOTD id
    Utilities.prototype.isMotdId = function (id) {
        return id.length == 27 && id.match(/^MID-/);
    };

    // Return true if the specified string is a rating category ID
    Utilities.prototype.isRatingCategoryId = function (id) {
        return id.length == 27 && id.match(/^CAT-/);
    };

    // Return true if the specified string is a content ID
    Utilities.prototype.isContentId = function (id) {
        return id.length == 27 && id.match(/^CON-/);
    };

    // Get the standard message for email aliases
    Utilities.prototype.getEmailAliasMessage = function (alias) {
        var message = '';
        switch (alias) {
            case 'support':
                message =
                    'Please provide below any other information that you ' +
                    'think might be helpful.  For example, you might ' +
                    'describe how you were using the app or even provide ' +
                    'a screenshot. Thanks for helping to make ' +
                    this.getApplicationName() + ' better!';
                break;
            case 'comments':
                message = '';
                break;
            case 'notify':
                message = '';
                break;
            case 'careers':
                message = '';
                break;
            case 'merchants':
                message = '';
                break;
            default:
                message = '';
        }
        return message;
    };

    // Generate email link to specified email alias with given subject & body
    Utilities.prototype.getEmailLink = function (alias, subject, body, id) {
        // Assemble mailto link
        var email = alias + '@'   + this.getEmailServerDomain() + '.com',
            header ='DEVICE: '    + this.getDeviceFromDeviceId() + '\n' +
                    'PLATFORM: '  + this.getOSFromDeviceId() + '\n' +
                    'BROWSER: '   + this.getBrowserFromDeviceId() + '\n\n' +
                    'DEVICE_ID: ' + this.getDeviceId() + '\n\n',
            link =  'mailto:' + email +
                    '?subject=' + encodeURIComponent(subject) +
                    '&body=' + encodeURIComponent(header + body);
        // Return html link with complete mailto href
        return '<a id=\"' + id + '\" href=\"' + link + '\">' + email + '</a>';
    };

    // Determine if underlying browser is unsupported (IE < 10)
    Utilities.prototype.isUnsupportedBrowser = function () {
        var unsupported = false,
            msie = navigator.userAgent.indexOf('MSIE '),
            msie1232 = navigator.userAgent.indexOf('x32'),
            msie1264 = navigator.userAgent.indexOf('x64');
        if (msie >= 0) {
            version = parseInt(navigator.userAgent.substring(
                msie + 5, navigator.userAgent.indexOf('.', msie)), 10);
            if (version < 10) {
                unsupported = true;
            }
        }
        if ((msie1232 >0) || (msie1264 >0) ||
            (!(window.ActiveXObject) &&
             "ActiveXObject" in window)) {  //explorer 11,12 check
            unsupported = true;
        }
        return unsupported;
    };

    // Determine if the browser is Microsoft Internet Explorer 10+
    Utilities.prototype.isSupportedIE = function () {
        // Make a list of IE 10+ styles
        var ie10Styles = [
            'msTouchAction',
            'msWrapFlow',
            'msWrapMargin',
            'msWrapThrough',
            'msOverflowStyle',
            'msScrollChaining',
            'msScrollLimit',
            'msScrollLimitXMin',
            'msScrollLimitYMin',
            'msScrollLimitXMax',
            'msScrollLimitYMax',
            'msScrollRails',
            'msScrollSnapPointsX',
            'msScrollSnapPointsY',
            'msScrollSnapType',
            'msScrollSnapX',
            'msScrollSnapY',
            'msScrollTranslation',
        ];
        // Iterate through the styles to see if elements have them
        var isIE10 = true;
        for (var i=0; i<ie10Styles.length; i++) {
            if (typeof document.body.style[ie10Styles[i]] === 'undefined') {
                isIE10 = false;
                break;
            }
        }
        // Return findings
        return isIE10;
    };

    // Retrieve the int command code for the specified command string
    Utilities.prototype.getCommandCode = function (commandCodeString) {
        return commandCodes[commandCodeString];
    };

    // Get the list of command codes
    Utilities.prototype.getCommandCodes = function () {
        return commandCodes;
    };

    // Use simple method to determine if running on mobile.
    Utilities.prototype.isMobile = function () {
        if (isMobile === undefined) {
            isMobile = mobileUtils.isMobile();
        }
        return isMobile;
    };

    // Check for Mobile App
    Utilities.prototype.isMobileApp = function () {
        return mobileUtils.isFileUrl();
    };

    // Check for IOS Platform
    Utilities.prototype.isIOS = function () {
        return mobileUtils.isIOS();
    };

    // Check for Android Platform
    Utilities.prototype.isAndroid = function () {
        return mobileUtils.isAndroid();
    };

    // Check for Android Emulator
    Utilities.prototype.isAndroidEmulator = function () {
        return mobileUtils.isAndroidEmulator();
    };

    // Get Android version
    Utilities.prototype.getAndroidVersion = function () {
        return mobileUtils.getAndroidVersion();
    };

    // Need to check for iPad
    Utilities.prototype.isIPad = function () {
        return mobileUtils.isIPad();
    };

    // Get the IOS version
    Utilities.prototype.getIOSVersion = function () {
        return mobileUtils.getIOSVersion();
    };

    // Determine if running in standalone (homescreen) mode
    Utilities.prototype.isStandalone = function () {
        var standalone = false;
        if (this.isMobile()) {
            if (this.isIOS()) {
                if (navigator.standalone) {
                    standalone = true;
                }
            } else if (this.isAndroid()) {
                if (document.fullScreenEnabled ||
                    document.mozFullScreenEnabled ||
                    document.webkitFullscreenEnabled) {
                    standalone = true;
                }
            }
        }
        return standalone;
    };

    // Check for file URL which is used in mobile app
    Utilities.prototype.isFileUrl = function () {
        return mobileUtils.isFileUrl();
    };

    // From a string array, return only they items that match
    // the specified string.
    Utilities.prototype.filterKeys = function (keys, matchString) {
        var matchingKeys = [];
        for (var i=0; i<keys.length; i++) {
            if (keys[i].indexOf(matchString) >= 0) {
                matchingKeys.push(keys[i]);
            }
        }
        return matchingKeys;
    };

     // Remove all options from an option object that is passed in
    Utilities.prototype.removeOptions = function (options) {
        while (options.length) {
            options.remove(0);
        }
    };

    // Remove child elements from a parent
    Utilities.prototype.removeChildNodes = function (parent) {
        if (parent && parent.tagName) {
            while (parent.hasChildNodes()) {
                parent.removeChild(parent.children[0]);
            }
        }
    };

    // Add specified rows and columns to given container
    Utilities.prototype.addTableCells = function (container, rows, cols) {
        var tr;
        for (var row=0; row<rows; row++) {
            tr = document.createElement('tr');
            for (var col=0; col<cols; col++) {
                tr.appendChild(document.createElement('td'));
            }
            container.appendChild(tr);
        }
    };

    // Add specified rows and columns to given container
    Utilities.prototype.addTableHeaderCells = function (container, rows, cols) {
        var tr;
        for (var row=0; row<rows; row++) {
            tr = document.createElement('tr');
            for (var col=0; col<cols; col++) {
                tr.appendChild(document.createElement('th'));
            }
            container.appendChild(tr);
        }
    };

    // Replace a table row with specified number of cells
    Utilities.prototype.replaceTableRow = function (row, emptyCellCount) {
        // Delete the existing cells in the row
        var cellCount = row.cells.length;
        for (var i=0; i<cellCount; i++) {
            row.deleteCell(0);
        }
        // Add new empty cells
        for (var i=0; i<emptyCellCount; i++) {
            row.insertCell(i);
        }
    };

    // Determine if web local storage is supported
    Utilities.prototype.isLocalStorageSupported = function () {
        // Assume it's false until we confirm
        var supported = false;
        // Have we already completed the test?
        if (typeof localStorageSupported != 'undefined') {
            supported = localStorageSupported;
        } else {
            try {
                // Check if localStorage object exists
                if ('localStorage' in window && window['localStorage'] !== null) {
                    // Great.  Now actually try to use it.
                    var testvalue = 'testvalue';
                    window.localStorage.setItem('testkey',testvalue);
                    if (window.localStorage.getItem('testkey') == testvalue) {
                        window.localStorage.removeItem('testkey');
                        supported = true;
                    }
                }
            } catch (e) {
                supported = false;
            }
            localStorageSupported = supported;
        }
        return supported;
    };

    // Has welcome message been read
    Utilities.prototype.isWelcomeRead = function () {
        var welcomeRead = true;
        if (typeof welcomeKey == 'undefined') {
            welcomeKey = 'com.' + applicationName.toLowerCase() + '.welcome';
        }
        if (this.isLocalStorageSupported()) {
            if (localStorage.getItem(welcomeKey) != 'true') {
                welcomeRead = false;
            }
        }
        return welcomeRead;
    };

    // Set welcome message boolean to specified value
    Utilities.prototype.setWelcomeRead = function (read) {
        if (typeof welcomeKey == 'undefined') {
            welcomeKey = 'com.' + applicationName.toLowerCase() + '.welcome';
        }
        if (this.isLocalStorageSupported()) {
            localStorage.setItem(welcomeKey, read);
        }
    };

    // Get the appropriate image for the key
    Utilities.prototype.getImage = function (key, prefix) {
        return images.getSrc(key, prefix);
    };

    // Set image attributes for given img and key
    Utilities.prototype.setImageAttributes = function (img, key, prefix) {
        images.setAttributes(img, key, prefix);
    };

    // Set area image
    Utilities.prototype.setAreaImage = function (img, country, area) {
        var imgUrl = './img/areas/' +
            country.toLocaleLowerCase().replace(/ /g,'_') + '/' +
            area.toLocaleLowerCase().replace(/ /g,'_') + '.png';
        // Fallback to a default image if the original is not found
        img.onerror = function (event) {
            this.onerror = null;
            img.src = './img/areas/default.png';
        };
        // Try to set the image
        img.src = imgUrl;
    };

    // Convert dataURI representation to a Blob instance
    Utilities.prototype.getBlob = function(dataURI) {
        var blob,
            mimetype,
            data,
            byteString,
            arrayBuffer,
            uint8Array,
            dataView,
            valid = false;

        // Validate
        if (dataURI == null || typeof dataURI === 'undefined') {
            console.log('ERROR: invalid dataURI!');
        } else {
            valid = true;
        }

        // Convert
        if (valid) {

            // Get the dataURI mimetype
            mimetype = dataURI.substring(5,dataURI.indexOf(';'));

            // Get the payload
            data = dataURI.substring(dataURI.indexOf(',') + 1);

            // Convert to binary
            byteString = atob(data);

            // Load in a typed array
            arrayBuffer = new ArrayBuffer(byteString.length);
            uint8Array = new Uint8Array(arrayBuffer);
            for (var i=0; i<byteString.length; i++) {
                uint8Array[i] = byteString.charCodeAt(i);
            }

            // Finally, make a Blob
            dataView = new DataView(arrayBuffer);
            blob = new Blob([dataView], {type: mimetype});
        }

        // Return the Blob instance or undefined
        return blob;
    };

    // Assemble score button
    Utilities.prototype.setScoreButton = function (
        containerDiv, score, textDiv, text) {

        // Assemble
        var scoreButtonDiv = document.createElement('div');
        this.removeChildNodes(containerDiv);
        containerDiv.appendChild(scoreButtonDiv);
        containerDiv.classList.add('score-button-div');
        scoreButtonDiv.classList.add('score-button-score-div');

        // Remove any existing styling
        for (var i=0; i<10; i++) {
            containerDiv.classList.remove('button' + i);
        }
        containerDiv.classList.remove('button-default');

        // Set background color based upon composite score
        var buttonScore = Math.round(score);
        switch (buttonScore) {

            // Button for 0-9 composite scores
            case 0: case 1: case 2: case 3: case 4:
            case 5: case 6: case 7: case 8: case 9:
                containerDiv.classList.add('button0');
                scoreButtonDiv.classList.add('button0');
                break;

            // Button for 10-19 composite scores
            case 10: case 11: case 12: case 13: case 14:
            case 15: case 16: case 17: case 18: case 19:
                containerDiv.classList.add('button1');
                scoreButtonDiv.classList.add('button1');
                break;

            // Button for 20-29 composite scores
            case 20: case 21: case 22: case 23: case 24:
            case 25: case 26: case 27: case 28: case 29:
                containerDiv.classList.add('button2');
                scoreButtonDiv.classList.add('button2');
                break;

            // Button for 30-39 composite scores
            case 30: case 31: case 32: case 33: case 34:
            case 35: case 36: case 37: case 38: case 39:
                containerDiv.classList.add('button3');
                scoreButtonDiv.classList.add('button3');
                break;

            // Button for 40-49 composite scores
            case 40: case 41: case 42: case 43: case 44:
            case 45: case 46: case 47: case 48: case 49:
                containerDiv.classList.add('button4');
                scoreButtonDiv.classList.add('button4');
                break;

            // Button for 50-59 composite scores
            case 50: case 51: case 52: case 53: case 54:
            case 55: case 56: case 57: case 58: case 59:
                containerDiv.classList.add('button5');
                scoreButtonDiv.classList.add('button5');
                break;

            // Button for 60-69 composite scores
            case 60: case 61: case 62: case 63: case 64:
            case 65: case 66: case 67: case 68: case 69:
                containerDiv.classList.add('button6');
                scoreButtonDiv.classList.add('button6');
                break;

            // Button for 70-79 composite scores
            case 70: case 71: case 72: case 73: case 74:
            case 75: case 76: case 77: case 78: case 79:
                containerDiv.classList.add('button7');
                scoreButtonDiv.classList.add('button7');
                break;

            // Button for 80-89 composite scores
            case 80: case 81: case 82: case 83: case 84:
            case 85: case 86: case 87: case 88: case 89:
                containerDiv.classList.add('button8');
                scoreButtonDiv.classList.add('button8');
                break;

            // Button for 90-100 composite scores
            case 90: case 91: case 92: case 93: case 94:
            case 95: case 96: case 97: case 98: case 99: case 100:
                containerDiv.classList.add('button9');
                scoreButtonDiv.classList.add('button9');
                if (buttonScore == 100) {
                    scoreButtonDiv.classList.add('score100');
                }
                break;

            default:
                containerDiv.classList.add('button-default');
                break;
        }

        // Set score
        scoreButtonDiv.innerHTML = '';
        if (buttonScore >= 0) {
            scoreButtonDiv.innerHTML = buttonScore;
        }

        // Set label for score button
        textDiv.innerHTML = text || '';
    };

    // Get Object keys, manually if necessary
    Utilities.prototype.getObjectKeys = function (target) {
        Object.keys = Object.keys || function (obj, key, keys) {
            var keys = [];
            for (key in obj) {
                if (obj.hasOwnProperty(key)) {
                    keys.push(key);
                }
            }
            return keys;
        };
        return Object.keys(target);
    };

    // Get Object values
    Utilities.prototype.getObjectValues = function (target) {
        var values = [];
        for (var key in target) {
            if (target.hasOwnProperty(key)) {
                values.push(target[key]);
            }
        }
        return values;
    };

    // Determine if specified object is empty
    Utilities.prototype.isEmpty = function (object) {
        var empty = true;
        if (object != null) {
            for (var entry in object) {
                if (object.hasOwnProperty(entry)) {
                    empty = false;
                    break;
                }
            }
        }
        return empty;
    };

    // Sort array and return results
    Utilities.prototype.sort = function (arr) {
         return arr.sort(function (a, b) {return a - b;});
    };

    // Sort object array in place
    Utilities.prototype.sortBy = function (arr, prop1, prop2) {
        if (arr) {
            arr.sort (function (a, b) {
                var returnValue = 0;
                if (a[prop1] !== b[prop1]) {
                    returnValue = (a[prop1] > b[prop1] ? 1 : -1);
                } else if (prop2) {
                    if (a[prop2] !== b[prop2]) {
                        returnValue = (a[prop2] > b[prop2] ? 1 : -1);
                    }
                }
                return returnValue;
            });
        }
    };

    // Reveal the API version for this client
    Utilities.prototype.getApiVersion = function () {
        return apiVersion;
    };

    // Return the name of the application, derived from meta tags
    Utilities.prototype.getApplicationName = function () {
        return applicationName;
    };

    // Set the app name, derived from meta tags
    Utilities.prototype.setApplicationName = function (appName) {
        // Set application name
        applicationName = appName;
        // Set application name for image URLs that include it
        images.setApplicationName(applicationName);
    };

    // Get the app version number for this client
    Utilities.prototype.getApplicationVersion = function () {
        return applicationVersion;
    };

    // Set the app version number for this client
    Utilities.prototype.setApplicationVersion = function (appVersion) {
        applicationVersion = appVersion;
    };

    // Return the unique id of the application
    Utilities.prototype.getApplicationId = function () {
        if (applicationId == null || typeof applicationId === undefined) {
            applicationId = this.stringHash(applicationName + 'Oauth');
        }
        return applicationId;
    };

    // Return the default id of the application
    Utilities.prototype.getDefaultApplicationId = function () {
        if (defaultApplicationId == null ||
            typeof defaultApplicationId === undefined) {
            defaultApplicationId = this.stringHash(defaultApplicationName + 'Oauth');
        }
        return defaultApplicationId;
    };

    // Return the application short description
    Utilities.prototype.getApplicationDescription = function () {
        return applicationDescription;
    };

    // Set the app description derived from meta tags
    Utilities.prototype.setApplicationDescription = function (appDescription) {
        applicationDescription = appDescription;
    };

    // Return the email server domain
    Utilities.prototype.getEmailServerDomain = function () {
        return emailServerDomain;
    };

    // Set the email server domain
    Utilities.prototype.setEmailServerDomain = function (newEmailServerDomain) {
        emailServerDomain = newEmailServerDomain;
    };

    // Get the company name
    Utilities.prototype.getCompanyName = function () {
        return companyName;
    };

    // Return the out of range message
    Utilities.prototype.getOutOfRangeText = function () {
        return outOfRangeText;
    };

    // Return the out of range message
    Utilities.prototype.getScoringRange = function () {
        return scoringRange;
    };

    // Return the no location services message
    Utilities.prototype.getNoLocationServicesText = function () {
        return noLocationServicesText;
    };

    // Get the window outer width (for several browsers and versions)
    Utilities.prototype.getWindowWidth = function () {
        return window.innerWidth ||
               document.documentElement.clientWidth ||
               document.body.clientWidth;
    };

    // Get the window width (for several browsers and versions)
    Utilities.prototype.getWindowOuterWidth = function () {
        return window.outerWidth ||
               document.documentElement.clientWidth ||
               document.body.clientWidth;
    };

    // Get the window height (for several browsers and versions)
    Utilities.prototype.getWindowHeight = function () {
        return window.innerHeight ||
               document.documentElement.clientHeight ||
               document.body.clientHeight;
    };

    // Get the window outer height (for several browsers and versions)
    Utilities.prototype.getWindowOuterHeight = function () {
        return window.outerHeight ||
               document.documentElement.clientHeight ||
               document.body.clientHeight;
    };

    // Get the orientation, assume portrait
    Utilities.prototype.isPortrait = function () {
        var portrait = true;
        if (window.orientation &&
            (window.orientation === 90 || window.orientation === -90)) {
            portrait = false;
        } else {
            if (window.innerHeight < window.innerHeight) {
                portrait = false;
            }
        }
        return portrait;
    };

    // Attach given event on the window object
    Utilities.prototype.setWindowEventListener = function (eventType, callback) {
        eventUtils.setWindowEventListener(eventType, callback);
    };

    // Get top notch size for device, if any
    Utilities.prototype.getTopNotchSize = function () {
        if (topNotchSize == null || typeof topNotchSize === 'undefined') {
            var div = document.createElement('div'),
                topNotchSize = 0;

            // Add dummy div
            document.body.appendChild(div);

            // Check for iPhone X notch on IOS 11.2+
            if (topNotchSize == 0) {
                div.style.paddingTop =
                    'env(safe-area-inset-top)';
                topNotchSize = parseInt(
                    window.getComputedStyle(div).paddingTop, 10);
            }

            // Check for iPhone X notch on IOS 11.0/11.1
            if (topNotchSize == 0) {
                div.style.paddingTop =
                    'constant(safe-area-inset-top)';
                topNotchSize = parseInt(
                    window.getComputedStyle(div).paddingTop, 10);
            }

            // Remove dummy div
            document.body.removeChild(div);
        }

        // Return notch size calculated
        return topNotchSize;
    };

    // Get bottom notch size for device, if any
    Utilities.prototype.getBottomNotchSize = function () {
        if (bottomNotchSize == null || typeof bottomNotchSize === 'undefined') {
            var div = document.createElement('div'),
                bottomNotchSize = 0;

            // Add dummy div
            document.body.appendChild(div);

            // Check for iPhone X notch on IOS 11.2+
            if (bottomNotchSize == 0) {
                div.style.paddingBottom = 'env(safe-area-inset-bottom)';
                bottomNotchSize = parseInt(
                    window.getComputedStyle(div).paddingBottom, 10);
            }

            // Check for iPhone X notch on IOS 11.0/11.1
            if (bottomNotchSize == 0) {
                div.style.paddingBottom = 'constant(safe-area-inset-bottom)';
                bottomNotchSize = parseInt(
                    window.getComputedStyle(div).paddingBottom, 10);
            }

            // Remove dummy div
            document.body.removeChild(div);
        }

        // Return notch size calculated
        return bottomNotchSize;
    };

    // Get the size of the status bar (new on IOS 7)
    Utilities.prototype.getStatusBarSize = function () {
        if (statusBarSize == null || typeof statusBarSize === 'undefined') {
            statusBarSize = 0;
            if (this.isIOS()) {
                var iosVersion = this.getIOSVersion();
                if (iosVersion && iosVersion >= 7.0 && this.isFileUrl()) {
                    if (this.isPortrait() || this.isIPad()) {

                        // Get notch size, if any
                        statusBarSize = this.getTopNotchSize();

                        // Default for all other iPhones/iPads
                        if (statusBarSize == 0) {
                            statusBarSize = 20;
                        }
                    }
                }
            }
        }

        // Return findings
        return statusBarSize;
    };

    // Retrieve the ad height for this environment
    Utilities.prototype.getAdHeight = function () {
        return 50;  // TODO: Adjust when desktop real ads enabled
    };

    // Scale font size until it will fit into the specified div
    // WARNING: This only works if the element is visible
    Utilities.prototype.getFontSizeToFit = function (container,
                                                     text,
                                                     defaultFontSize) {
        // Get current container style
        var style = getComputedStyle(container);
        var fontSize = parseInt(style.fontSize, 10);
        if (defaultFontSize) {
            fontSize = parseInt(defaultFontSize, 10);
        }
        // Make sure the text fits in the container
        if (container.clientWidth > 0) {
            // Create hidden div to use
            var hiddenDiv = document.createElement('div');
            hiddenDiv.style.position = 'absolute';
            hiddenDiv.style.left = '-999em';
            hiddenDiv.style.whiteSpace = 'nowrap';
            hiddenDiv.style.letterSpacing = style.letterSpacing;
            hiddenDiv.style.fontFamily = style.fontFamily;
            hiddenDiv.style.fontWeight = style.fontWeight;
            hiddenDiv.style.fontSize = fontSize + 'px';
            hiddenDiv.innerHTML = text;
            // Add hidden element to body
            document.body.appendChild(hiddenDiv);
            // If text does not fit, decrease font size until it does
            var padding = parseInt(style.paddingLeft) +
                    parseInt(style.paddingRight),
                maxWidth = container.clientWidth - padding;
            while (hiddenDiv.clientWidth > maxWidth && fontSize > 0) {
                fontSize -= 1;
                hiddenDiv.style.fontSize = fontSize + 'px';
            }
            // Remove hidden element and update container font size that fits
            document.body.removeChild(hiddenDiv);
        }
        // Return the font size that will fit text in the container
        return fontSize + 'px';
    };

    // Set font size that will fit in specified div
    Utilities.prototype.setFontSizeToFit = function (container,
                                                     text,
                                                     defaultSize) {
        setTimeout(function () {
            container.style.fontSize =
                this.getFontSizeToFit(container, text, defaultSize);
        }.bind(this), 1);
    };

    // Get the size of the mobile browser header (address bar)
    Utilities.prototype.getMobileBrowserHeaderSize = function () {
        var size = 0;
        return size;
    };

    // Get the size of the mobile browser footer (address bar)
    Utilities.prototype.getMobileBrowserFooterSize = function () {
        var size = 0;
        if (this.isMobile()) {
            if (this.isIOS()) {
                if (!this.isFileUrl()) {
                    size = 52;
                }
            }
        }
        return size;
    };

    // Get scroll position of element
    Utilities.prototype.getScrollPosition = function (element) {
        var curtop = 0;
        if (element.offsetParent) {
            do {
                curtop += element.offsetTop;
            } while (element = element.offsetParent);
        }
        return curtop;
    };

    // Get transition event for this platform
    Utilities.prototype.getTransitionEvent = function () {
        return eventUtils.getTransitionEvent();
    };

    // Determine if device is touch enabled
    Utilities.prototype.isTouchable = function () {
        return eventUtils.isTouchable();
    };

    // Get the supported event types for this device
    Utilities.prototype.getSupportedEventTypes = function () {
        return eventUtils.getSupportedEventTypes();
    };

    // Add click or touch listeners based on device
    Utilities.prototype.addEventListeners = function (element, callback) {
        eventUtils.addEventListeners(element, callback);
    };

    // Add swipe listeners based on device
    Utilities.prototype.addSwipeEventListeners = function (element, callback) {
        eventUtils.addSwipeEventListeners(element, callback);
    };

    // Detect if a particular feature is supported in this env
    Utilities.prototype.isCSSFeatureSupported = function (featureName) {
        var hasFeature, vendorPrefixes, testElement;
        var featureNameCapital, featureNameCamel, featureNameLower;

        // Possible vendor prefixes (until all support standard)
        vendorPrefixes = 'webkit Webkit Moz moz ms O'.split(' ');

        // Create possible variations
        featureNameLower = featureName.toLowerCase();
        featureNameCamel = featureName.replace(/-([a-z])/g, function (f) {
            return f[1].toUpperCase();
        });

        featureNameCapital = featureNameCamel.charAt(0).toUpperCase() +
            featureNameCamel.substr(1);

        // Create test element in which to probe features
        testElement = document.createElement('div');

        // Assume feature is not supported until proven
        hasFeature = false;

        // First test if the feature is directly supported, then try prefixes
        if (typeof testElement.style[featureName] != 'undefined' ||
            typeof testElement.style[featureNameCamel] != 'undefined') {
            hasFeature = true;
        } else {
            // Test each possible vendor prefix
            var styleFeature;
            for (var i=0; i<vendorPrefixes.length; i++ ) {
                styleFeature = vendorPrefixes[i] + featureNameCapital;
                if (testElement.style[styleFeature] !== undefined ) {
                    hasFeature = true;
                    break;
                }
            }
        }

        // Return final determination
        return hasFeature;
    };

    // Determine if standard CustomEvent is supported
    Utilities.prototype.isCustomEventSupported = function () {
        return eventUtils.isCustomEventSupported();
    };

    // Toggle class name on an element
    Utilities.prototype.toggleClassName = function (element, className) {
        if (element && className) {
            if (element.classList.contains(className)) {
                element.classList.remove(className);
            } else {
                element.classList.add(className);
            }
        }
    };

    // Get photo url from PhotoObject or Favorite
    Utilities.prototype.getPhotoUrl = function (photo) {
        // Default if there is no getUrl() or url property
        var url = this.getImage('no-score');
        // Get the photo dynamically if possible, or get fixed url if saved
        if (photo.url) {
            url = photo.url;
        } else {
            if (photo.getUrl) {
                url = photo.getUrl({
                    'maxWidth': this.getWindowWidth(),
                    'maxHeight': this.getWindowHeight()});
            }
        }
        return url;
    };

    // Quick test to determine if a URL is a dataURI
    Utilities.prototype.isDataURI = function (url) {
        return url && url.length >= 5 && url.substr(0,5) == 'data:';
    };

    // Get basePath, baseName, and file extension from URL
    Utilities.prototype.getURLComponents = function (url) {

        var noQueryArgs,
            basePath,
            baseName,
            extension,
            components = {
                basePath: undefined,
                baseName: undefined,
                extension: undefined
            };

        // Parse URL to get components
        if (url) {
            // Remove query arguments from the url
            noQueryArgs = url.split('#')[0].split('?')[0];

            // Get the base path
            basePath = noQueryArgs.substring(0, noQueryArgs.lastIndexOf('/'));

            // Get the base name of the file
            baseName = noQueryArgs.substring(basePath.length+1,
                                             noQueryArgs.lastIndexOf('.'));

            // Get the filename extension
            extension = noQueryArgs.split('.').pop();

            // Assemble
            components.basePath = basePath;
            components.baseName = baseName;
            components.extension = extension;
        }

        // Return parsed URL
        return components;
    };

    // Calculate the place distance and units from location
    Utilities.prototype.calculateDistanceAndUnits = function (displayMiles, meters) {
        var distance, units;
        if (displayMiles) {
            if (meters > metersPerTenthMile) {
                units = 'Miles';
                distance = meters / metersPerMile;
            } else {
                units = 'Feet';
                distance = meters * feetPerMeter;
            }
        } else {
            if (meters > metersPerTenthMile) {
                units = 'KMeters';
                distance = meters / metersPerKilometer;
            } else {
                units = 'Meters';
                distance = meters;
            }
        }
        return [distance, units];
    };

    // Calculate composite score from ratings and category weights.
    // Exclude any categories not in the selectedIds filter.
    Utilities.prototype.calculateCompositeScore = function (
        ratings, supportedRatingCategories, selectedIds) {

        var gradeSum,
            gradeCount,
            ratingCategory,
            categoryGrades,
            categoryGradeAverage,
            categoryWeight,
            weightedSum = 0,
            weightedCount = 0,
            compositeScore = 0;

        // Calculate composite score based upon rating counts
        for (var categoryId in ratings) {

            // Only include category in composite score if not filtered
            if (selectedIds == null ||
                typeof selectedIds === 'undefined' ||
                selectedIds.indexOf(categoryId) >= 0) {

                // Reset grade totals
                gradeSum = 0;
                gradeCount = 0;

                // Get category averages based on grade counts
                categoryGrades = ratings[categoryId];
                for (var grade in categoryGrades) {
                    gradeSum += parseInt(grade) * categoryGrades[grade];
                    gradeCount += categoryGrades[grade];
                }

                // Calculate category average
                categoryGradeAverage = gradeSum / gradeCount;

                // Get rating category weight
                categoryWeight = 1.0;
                for (var ratingCategoryId in supportedRatingCategories) {
                    ratingCategory = supportedRatingCategories[ratingCategoryId];
                    if (ratingCategory.getId() == categoryId) {
                        categoryWeight = ratingCategory.getWeight();
                        break;
                    }
                }

                // Capture weighted numbers
                weightedSum += categoryWeight * categoryGradeAverage;
                weightedCount += categoryWeight;
            }
        }

        // Finally calculate composite score
        if (weightedCount > 0) {
            compositeScore = weightedSum / weightedCount;
        }

        // Return composite score (weighted average)
        return compositeScore;
    };

    // One-way hashcode for strings
    Utilities.prototype.stringHash = function (text) {
        var character, hashCode, hashString = text;
        if (typeof text != 'undefined') {
            hashCode = 0;
            if (text.length > 0) {
                for (var i=0; i<text.length; i++) {
                    character = text.charCodeAt(i);
                    hashCode = ((hashCode<<5)-hashCode)+character;
                    hashCode = hashCode & hashCode; // Convert to 32bit integer
                }
                hashString = hashCode.toString();
            }
        }
        return hashString;
    };

    // Debug utility
    Utilities.prototype.printObj = function (obj) {
        console.log('Printing obj...');
        console.log(obj);
        for (var prop in obj) {
            if (obj.hasOwnProperty(prop)) {
                console.log(prop + ' = ' + obj[prop]);
            }
        }
    };

    // Sanitize html to remove exploitable tags & attributes
    Utilities.prototype.sanitize = function (html, tags) {
        // Use tags specified or default
        var defaultTags = ['br','p','font'],
            tags = tags || defaultTags;
        // Create a function to define custome tag policy
        function customTagPolicy(tagName, attribs) {
            var tag = tagName.toLowerCase(),
                tagPolicy = null;
            // Only accept <br> and <p> tags in html
            if (tags.indexOf(tag) >= 0) {
                tagPolicy = basicTagPolicy.call(this, tagName, attribs);
            }
            // Return policy for given tag
            return tagPolicy;
        };
        // Sanitize the html with the custom tag policy
        var sanitizedHtml =
                window.html.sanitizeWithPolicy(html, customTagPolicy);
        // Revert <>& back to their original chars
        sanitizedHtml = sanitizedHtml
            .replace(/&lt;/g,'<')
            .replace(/&gt;/g,'>')
            .replace(/&amp;/g,'&');
        // Return fully sanitized html string
        return sanitizedHtml;
    };
    // Sanitize html to remove ALL tags & attributes
    Utilities.prototype.sanitizeAll = function (html) {
        sanitizedHtml = html;
        // Only santize if needed
        if (html != null) {
            // Create a function to define custom tag policy
            function customTagPolicy(tagName, attribs) {
                return null;
            };
            // Sanitize the html with the custom tag policy
            var sanitizedHtml =
                    window.html.sanitizeWithPolicy(html, customTagPolicy);
            // Revert <>& back to their original chars
            sanitizedHtml = sanitizedHtml
                .replace(/&lt;/g,'<')
                .replace(/&gt;/g,'>')
                .replace(/&amp;/g,'&');
        }
        // Return fully sanitized html string
        return sanitizedHtml;
    };

    // Encode comments with some simple rules
    Utilities.prototype.encodeComment = function (comment) {
        var encodedComment = comment;
        // Get rid of chars that will not JSON unpack and are not encoded
        // with encodeURIComponent();
        if (encodedComment) {
            encodedComment = encodedComment.replace(/(\r\n|\n|\r)/gm, '\u2665');
            // Currently the '~' and '\\' does not break anything or behave unexpectedly
            //encodedComment = encodedComment.replace(/~/g, '');
            //encodedComment = encodedComment.replace(/\\/g, '');
            encodedComment = encodedComment.replace(/"/g, '\u2666');
        }
        return encodedComment;
    };

    // Decode comments that were earlier encoded as above
    Utilities.prototype.decodeComment = function (comment) {
        var decodedComment = comment;
        if (decodedComment) {
            decodedComment = decodedComment.replace(/\u2665/g, '\n');
            decodedComment = decodedComment.replace(/\u2666/g, '"');
        }
        return decodedComment;
    };

    // Check if string is all ASCII
    Utilities.prototype.isASCII = function (text) {
        return /^[\x00-\x7F]*$/.test(text);
    };

    // Check if string is all extended ASCII
    Utilities.prototype.isExtendedASCII = function (text) {
        return /^[\x00-\xFF]*$/.test(text);
    };

    // Remove non-ascii chars from string
    Utilities.prototype.removeNonASCII = function (text) {
        return text.replace(/[\u0080-\uffff]/g, '');
    };

    // Return true if character is a smart quote character
    Utilities.prototype.isSmartQuote = function (char) {
        return smartQuotesMap[char.charCodeAt(0)] != null;
    };

    // Convert browser "smart quotes" to regular quotes as entered
    Utilities.prototype.getRegularQuote = function (char) {
        return smartQuotesMap[char.charCodeAt(0)];
    };

    // Check if permissions have been granted
    Utilities.prototype.arePermissionsGranted = function (requested, granted) {
        var allGranted = false;
        if (requested != null && typeof requested != 'undefined' &&
            granted != null && typeof granted != 'undefined' &&
            requested.scope && granted.scope) {
            var requestedPermissions = requested.scope.split(','),
                grantedPermissions = granted.scope.split(','),
                found = true;
            for (var i=0; i<requestedPermissions.length; i++) {
                if (grantedPermissions.indexOf(requestedPermissions[i]) < 0) {
                    found = false;
                    break;
                }
            }
            allGranted = found;
        }
        return allGranted;
    };

    // Get query arg with given name
    // http://stackoverflow.com/questions/901115/how-can-i-get-query-string-values-in-javascript
    Utilities.prototype.getParameterByName = function (name) {
        name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
        var regex = new RegExp('[?&]' + name + '=([^&#]*)'),
            results = regex.exec(location.search);
        return results ? decodeURIComponent(results[1].replace(/\+/g, ' ')) : null;
    };

    // Get valid date from 'YYYY-MM-DD HH:MM:SS' or null if invalid
    Utilities.prototype.getDateFromString = function (dateString) {
        // Parse date string and return valid compnents or null
        var dateTime = null,
            year = null,
            month = null,
            day = null,
            hour = null,
            minute = null,
            second = null;
        if (dateString && typeof dateString === 'string') {
            dateTime = dateString.split(' ');
            if (dateTime && dateTime.length === 2) {
                // Get date portion
                var date = dateTime[0].split('-');
                if (date.length === 3) {
                    if (date[0].length === 4) {
                        year = parseInt(date[0], 10);
                    }
                    if (date[1].length === 2) {
                        month = parseInt(date[1], 10)-1;
                    }
                    if (date[2].length === 2) {
                        day = parseInt(date[2],10);
                    }
                }
                // Get time portion
                var time = dateTime[1].split(':');
                if (time.length === 3) {
                    if (time[0].length === 2) {
                        hour = parseInt(time[0], 10);
                    }
                    if (time[1].length === 2) {
                        minute = parseInt(time[1], 10);
                    }
                    if (time[2].length === 2) {
                        second = parseInt(time[2], 10);
                    }
                }
            }
        }
        // Create date from components
        var validDate = null;
        if (year != null && month != null && day != null &&
            hour != null && minute != null && second != null) {
            validDate = new Date(year, month, day, hour, minute, second, 0);
        }
        // Return valid date or null
        return validDate;
    };

    // Construct a date string from a Date instance
    Utilities.prototype.getStringFromDate = function (date) {
        var dateString = '',
            year,
            month,
            day,
            hour,
            minute,
            second;

        // Get year as YYYY
        year = date.getFullYear() + '';

        // Get month as MM
        month = date.getMonth() + 1 + '';
        if (date.getMonth() < 9) {
            month = '0' + month;
        }

        // Get day as DD
        day = date.getDate() + '';
        if (day.length < 2) {
            day = '0' + day;
        }

        // Get hour as HH
        hour = date.getHours() + '';
        if (hour.length < 2) {
            hour = '0' + hour;
        }

        // Get minute as MM
        minute = date.getMinutes() + '';
        if (minute.length < 2) {
            minute = '0' + minute;
        }

        // Get second as SS
        second = date.getSeconds() + '';
        if (second.length < 2) {
            second = '0' + second;
        }

        // Assemble and return
        dateString += year + '-';
        dateString += month + '-';
        dateString += day + ' ';
        dateString += hour + ':';
        dateString += minute + ':';
        dateString += second;

        return dateString;
    };

    // Construct a formal date string from date instance
    Utilities.prototype.getDateStringFromDate = function (date) {
        var year = date.getFullYear(),
            month = date.getMonth(),
            day = date.getDate(),
            monthString;

        // Get month string
        switch (month) {
            case 0:  monthString = 'January';   break;
            case 1:  monthString = 'February';  break;
            case 2:  monthString = 'March';     break;
            case 3:  monthString = 'April';     break;
            case 4:  monthString = 'May';       break;
            case 5:  monthString = 'June';      break;
            case 6:  monthString = 'July';      break;
            case 7:  monthString = 'August';    break;
            case 8:  monthString = 'September'; break;
            case 9:  monthString = 'October';   break;
            case 10: monthString = 'November';  break;
            case 11: monthString = 'December';  break;
        }

        // Assemble and return
        return monthString + ' ' + day + ', ' + year;
    };

    // Get next month from specified date
    Utilities.prototype.getNextMonth = function (beginDate) {
        var nextMonth = (beginDate.getMonth()+1)%12,
            nextYear = beginDate.getFullYear() +
                (nextMonth === 0 ? 1 : 0);
        beginDate.setMonth(nextMonth);
        beginDate.setFullYear(nextYear);
        return beginDate;
    };

    // Get next year from specified date
    Utilities.prototype.getNextYear = function (beginDate) {
        var nextYear = beginDate.getFullYear() + 1;
        beginDate.setFullYear(nextYear);
        return beginDate;
    };

    // Set a message in the message container
    Utilities.prototype.setMessage = function (container, message, messageType) {
        // Verify container exists
        if (container) {
            // Clear old message, if any
            if (container.childNodes && container.childNodes.length > 0) {
                var nodes = container.childNodes.length;
                for (var i=0; i<nodes; i++) {
                    container.removeChild(container.childNodes[0]);
                }
            }
            // Show new message, if desired
            var messageDiv;
            if (message && message.length > 0) {
                // Validate message type
                if (messageTypes.indexOf(messageType) >= 0) {
                    // Add new message
                    messageDiv = document.createElement('div');
                    messageDiv.innerHTML = message;
                    messageDiv.setAttribute('class', messageType);
                } else {
                    this.log('ERROR', 'invalid message type - ' + messageType);
                }
            }
            // Add message to container, if there is one to show
            if (messageDiv) {
                container.appendChild(messageDiv);
            }
        }
    };

    // Update place count string for specified element
    Utilities.prototype.updatePlaceCount = function (element, newCount) {
        // Replace number with newCount
        if (element && element.innerHTML != '') {
            // Find first instance of '/' and prepend with new count
            var slashIndex = element.innerHTML.indexOf('/');
            var countString;
            if (slashIndex < 0) {
                countString = element.innerHTML;
            } else {
                countString = element.innerHTML.substring(slashIndex+1);
            }
            // If new count is -1, that means we are done.  Display just count.
            if (newCount >= 0) {
                element.innerHTML = newCount + '/' + countString;
            } else {
                element.innerHTML = countString;
            }
        }
    };

    // Initialize elapsed time marker
    Utilities.prototype.resetElapsedTime = function () {
        elapsedTime = new Date().getTime();
    };

    // Calculate elapsed time relative to marker
    Utilities.prototype.getElapsedTime = function () {
        var diff = 0;
        if (elapsedTime) {
            diff = new Date().getTime() - elapsedTime;
        }
        return diff;
    };

    // Store performance statistics for tag
    Utilities.prototype.storePerformance = function (tag, value) {
        // Initialize tag array if necessary
        if (typeof perfStats[tag] === 'undefined') {
            perfStats[tag] = [];
        }
        // Store value
        perfStats[tag].push(value);
    };

    // Remove performance statistics for tag
    Utilities.prototype.removePerformance = function (tag) {
        if (typeof perfStats[tag] != 'undefined') {
            delete perfStats[tag];
        }
    };

    // Report performance statistics for each tag, or all if undefined
    Utilities.prototype.reportPerformance = function (tag) {
        // Get specified tag, or get all tags if undefined
        var tags = [tag];
        if (!tag) {
            tags = this.getObjectKeys(perfStats);
        }
        // Report average performance on each tag, if it exists
        var length = tags.length;
        for (var i=0; i<length; i++) {
            var tagName = tags[i];
            if (typeof perfStats[tagName] != 'undefined') {

                // Calculate
                var data = perfStats[tagName],
                    count = data.length,
                    sum = data.reduce(function(a, b) { return a + b; }, 0),
                    average = Math.round(sum/count),
                    message = '{ name: ' + tagName + ',' +
                              '  count: ' + count + ',' +
                              '  average: ' + average + ',' +
                              '  values: ' + data + ' }';

                // Report
                this.log('performance', message);
            }
        }
    };

    // Report information to the console log
    Utilities.prototype.log = function(type, message) {
        if (this.isLoggingEnabled()) {
            // Determine the message prefix based on type
            var prefix = '';
            switch (type.toLowerCase()) {
                case 'performance':
                    prefix = 'PERFORMANCE';
                    break;
                case 'error':
                    prefix = 'ERROR';
                    break;
                case 'warning':
                    prefix = 'WARNING';
                    break;
                case 'info':
                    prefix = 'INFO';
                    break;
            }
            console.log(prefix + ': ' + message);
        }
    };

    // Get the default name for a place before details are fetched
    Utilities.prototype.getDefaultPlaceName = function () {
        return defaultPlaceName;
    };

    // Get the default displayName
    Utilities.prototype.getDefaultDisplayName = function () {
        return defaultDisplayName;
    };

    // Get default map position
    Utilities.prototype.getDefaultPosition = function () {
        return defaultPosition;
    };

    // Get the approximate height of an element before adding it to the DOM
    Utilities.prototype.getOrphanHeight = function (orphanElement) {
        var elementDiv = document.createElement('div');
        elementDiv.style.left = '100%';
        elementDiv.appendChild(orphanElement);
        document.body.appendChild(elementDiv);
        var height = elementDiv.clientHeight;
        document.body.removeChild(elementDiv);
        elementDiv.removeChild(orphanElement);
        return height;
    };

    // Spin specified element
    Utilities.prototype.spinElement = function (element, direction, speed, start, end) {
        var degrees = start;
        var increment = 5;
        var spin = function (degrees) {
            var transform = 'rotate(' + (degrees*direction) + 'deg)';
            element.style.webkitTransform = transform;
            element.style.mozTransform = transform;
            element.style.msTransform = transform;
            element.style.transform = transform;
            degrees += increment;
            if (degrees <= end) {
                setTimeout(function() {
                    spin(degrees);
                }, speed);
            }
        };
        spin(degrees);
    };

    // Twist specified element
    Utilities.prototype.twistElement = function (element, direction, speed, start, end) {
        var degrees = start;
        var increment = 5;
        var twist = function (degrees) {
            var transform = 'rotateY(' + (degrees*direction) + 'deg)';
            element.style.webkitTransform = transform;
            element.style.mozTransform = transform;
            element.style.msTransform = transform;
            element.style.transform = transform;
            degrees += increment;
            if (degrees <= end) {
                setTimeout(function() {
                    twist(degrees);
                }, speed);
            }
        };
        twist(degrees);
    };

    // Return true if child is descendant of parent element
    Utilities.prototype.isDescendant = function (parent, child) {
        var node = child.parentNode;
        var descendant = false;
        while (node != null) {
            if (node == parent) {
                descendant = true;
                break;
            }
            node = node.parentNode;
        }
        return descendant;
    };

    // Get accelerometer flag
    Utilities.prototype.getHasAccelerometer = function () {
        return hasAccelerometer;
    };

    // Set accelerometer flag
    Utilities.prototype.setHasAccelerometer = function (newHasAccelerometer) {
        hasAccelerometer = newHasAccelerometer;
    };

    // Determine if this device is eligible to submit scores
    Utilities.prototype.getCanScore = function () {
        if (canScore === undefined) {
            canScore = true;
            if (this.isAndroid() &&
                (!this.getHasAccelerometer() ||
                 navigator.userAgent.match(/[; ]*sdk /) ||
                 navigator.userAgent.match(/[; ]*google_sdk /))) {
                canScore = false;
            }
        }
        return canScore;
    };

    // True if logging is enabled
    Utilities.prototype.isLoggingEnabled = function () {
        return loggingEnabled;
    };

    // Show an alert message
    Utilities.prototype.showAlert = function (message, title, image, callback) {
        // Set the message
        alertPanel.setMessage(message);

        // Set the title for the alert
        alertPanel.setTitle(title);

        // Set the image for the alert
        alertPanel.setImage(image);

        // Override default close button listener to enable callback
        var closeDiv = alertPanel.getCloseDiv();
        eventUtils.addEventListeners(closeDiv, function () {
            this.hideAlert(function () {
                if (callback) {
                    callback();
                }
            });
        }.bind(this));
        closeDiv.onkeyup = function (event) {
            if (event.keyCode === 13 || event.keyCode === 32) {
                this.hideAlert(function () {
                    if (callback) {
                        callback();
                    }
                });
            }
        }.bind(this);

        // Showtime!
        alertPanel.show();
    };

    // Show an alert message that contains an active email link.  The standard
    // alert message has a overlay mask that captures click events and discards
    // them.  Thus, an indirect approach is needed to take the link action.
    Utilities.prototype.showAlertWithEmailLink = function (
        message, emailLinkId, title, image) {
        // Show the alert message
        this.showAlert(message, title, image);

        // Activate email link shown in the message via a temp anchor.
        // Provide a sufficiently long wait time to allow email link
        // to get added to the DOM and accessible via element id.
        setTimeout(function () {
            var emailLink = document.getElementById(emailLinkId);
            if (emailLink) {
                // Add replacement listener to link to capture events
                eventUtils.addEventListeners(emailLink, function () {
                    // Create a new temporary anchor
                    var anchor = document.createElement('a');
                    // Set it's onclick action href to the original email link
                    anchor.href = emailLink.href;
                    // Add link to document body to avoid mask
                    document.body.appendChild(anchor);
                    // Click on the link programmatically
                    anchor.click();
                    // Finally, remove the temp link from document body
                    document.body.removeChild(anchor);
                }.bind(this));
            }
        }.bind(this), 1000);
    };

    // Hide the alert message
    Utilities.prototype.hideAlert = function (callback) {
        // Reset the alert panel
        setTimeout(function () {
            alertPanel.reset();
        }.bind(this), 500);
        alertPanel.hide(function () {
            if (callback) {
                callback();
            }
        });
    };

    // Get specified address component, client can choose long/short name
    Utilities.prototype.getAddressComponent = function (components, name) {
        var component;
        for (var i=0; i<components.length; i++) {
            for (var j=0; j<components[i].types.length; j++) {
                if (components[i].types[j] == name) {
                    component = components[i];
                    break;
                }
            }
            if (component) {
                break;
            }
        }
        return component;
    };

    // Get the place's street address
    Utilities.prototype.getPlaceStreetAddress = function (place) {
        var address, streetNumber, streetRoute, premise, streetAddress;
        if (place) {
            // Handle both Google Maps and User Defined Places
            if (place.address_components) {
                streetNumber =
                    this.getAddressComponent(place.address_components,
                        'street_number');
                streetRoute =
                    this.getAddressComponent(place.address_components,
                        'route');
                address =
                    (streetNumber ? streetNumber.long_name + ' ' : '') +
                    (streetRoute  ? streetRoute.long_name  : '');
                // If Google does not provide a street address, try premise
                if (!address) {
                    premise =
                        this.getAddressComponent(place.address_components,
                            'premise');
                    address = premise ? premise.long_name : undefined;
                }
            } else if (place.locationStreet) {
                address = place.locationStreet;
            }
            if (address) {
                streetAddress = { short_name: address, long_name: address };
            }
        }
        return streetAddress;
    };

    // Get the place's locality
    Utilities.prototype.getPlaceLocality = function (place) {
        var locality;
        if (place) {
            // Handle both Google Maps and User Defined Places
            if (place.address_components) {
                locality =
                    this.getAddressComponent(place.address_components,
                        'locality');
            } else if (place.locationLocality) {
                locality = { short_name: place.locationLocality,
                             long_name:  place.locationLocality };
            }
        }
        return locality;
    };

    // Get the place's area
    Utilities.prototype.getPlaceArea = function (place) {
        var area;
        if (place) {
            // Handle both Google Maps and User Defined Places
            if (place.address_components) {
                area =
                    this.getAddressComponent(place.address_components,
                        'administrative_area_level_1');
            } else if (place.locationArea) {
                area = { short_name: place.locationArea,
                         long_name:  place.locationArea };
            }
        }
        return area;
    };

    // Get the place's postal code
    Utilities.prototype.getPlaceCode = function (place) {
        var code;
        if (place) {
            // Handle both Google Maps and User Defined Places
            if (place.address_components) {
                code =
                    this.getAddressComponent(place.address_components,
                        'postal_code');
            } else if (place.locationCode) {
                code = { short_name: place.locationCode,
                         long_name:  place.locationCode };
            }
        }
        return code;
    };

    // Get the place's country
    Utilities.prototype.getPlaceCountry = function (place) {
        var country;
        if (place) {
            // Handle both Google Maps and User Defined Places
            if (place.address_components) {
                country =
                    this.getAddressComponent(place.address_components,
                        'country');
            } else if (place.locationArea) {
                country = { short_name: place.locationCountry,
                            long_name:  place.locationCountry };
            }
        }
        return country;
    };

    // Get one of the main views given it's name
    Utilities.prototype.getView = function (main, viewName) {
        var views = main.getViews();
        return views[viewName];
    };

    // Shallow compare two objects
    Utilities.prototype.areEqualShallow = function (a, b) {
        var equal = true;
        for (var key in a) {
            if (!(key in b) ||
                JSON.stringify(a[key]) !== JSON.stringify(b[key])) {
                equal = false;
            }
        }
        for (var key in b) {
            if (!(key in a) ||
                JSON.stringify(a[key]) !== JSON.stringify(b[key])) {
                equal = false;
            }
        }
        return equal;
    };

    // Determine if an object can be represented by a number
    Utilities.prototype.isNumeric = function (obj) {
          return !isNaN(parseFloat(obj)) && isFinite(obj);
    };

    // Format latitude/longitude to the number of decimal places.
    Utilities.prototype.formatLatLng = function (loc, digits) {
        // Convert to number and check sign
        var locString,
            locNumber = parseFloat(loc),
            sign = (locNumber < 0 ? -1 : 1),
            precision = digits || defaultLatLngPrecision;

        // Ensure positive number as toFixed does not support negative
        locNumber = locNumber * sign;

        // Set precision to specified decimal places
        locString = locNumber.toFixed(precision);

        // Adjust sign if necessary and return
        if (sign < 0) {
            locString = '-' + locString;
        }
        return parseFloat(locString);
    };

    // Capitalize first letter of string
    Utilities.prototype.capitalizeFirstLetter = function (text) {
        return text.charAt(0).toUpperCase() + text.slice(1);
    };

    // Get place formatted address or GPS location
    Utilities.prototype.getAddressOrGPS = function (place) {
        var address = place.formatted_address || place.locationAddress;
        if (this.isUserDefinedLocation(place.place_id || place.id)) {
            if (place.findByAddress == false) {
                address = 'GPS: ' +
                    this.formatLatLng(place.position.latitude) +
                    ', ' +
                    this.formatLatLng(place.position.longitude);
            } else {
                address = place.locationAddress;
            }
        }
        return address;
    };

    // Determine if a place id is from a user defined location
    Utilities.prototype.isUserDefinedLocation = function (id) {
        return /^UDL/.test(id);
    };

    // Format a single line complete address for multi-line display
    Utilities.prototype.formatAddressForDisplay = function (placeAddress) {
        var formattedAddress = placeAddress,
            firstComma = placeAddress.indexOf(','),
            lastComma = placeAddress.lastIndexOf(',');

        // Check for valid address and format
        if (firstComma >= 0 && lastComma >= firstComma) {
            formattedAddress = placeAddress.substring(
                0,firstComma).trim();
            formattedAddress += '\n';
            formattedAddress += placeAddress.substring(
                firstComma+1, lastComma).trim();
            formattedAddress += '\n';
            formattedAddress += placeAddress.substring(
                lastComma+1).trim();
        }

        // Trim and send
        formattedAddress =
            formattedAddress.replace(/^\s+|\s+$/g,'');
        return formattedAddress;
    };

    // Run array of functions in order via nested callbacks
    Utilities.prototype.runFunctionsWithCallback =
        function (functionArray, callback) {

        // Define chaining function
        var chain = function (currentFunction) {
            if (currentFunction) {
                // Call the chainedfunction
                currentFunction(function () {
                    chain(functionArray.shift());
                });
            } else {
                // We're done, so callback if requested
                if (callback) {
                    callback();
                }
            }
        };

        // Call the first function to start the chain
        chain(functionArray.shift());
    };

    // Combine two arrays of objects
    Utilities.prototype.mergeArrays = function (array1, array2) {
        // Create a merged array
        var mergedArray = [];

        // Walk arrays to find elements not found in both
        for (var i in array1) {
            var found = false;
            for (var j in array2) {
                if (array1[i].place_id === array2[j].place_id) {
                    found = true;
                    break;
                }
            }
            if (!found) {
                mergedArray.push(array1[i]);
            }
        }
        // Add shared elements to get a merged result
        mergedArray = mergedArray.concat(array2);

        // Return merged result
        return mergedArray;
    };

    // Shuffle an array of objects
    // From: http://stackoverflow.com/questions/2450954/
    //       how-to-randomize-shuffle-a-javascript-array
    Utilities.prototype.shuffle = function (array) {
        var currentIndex = array.length, temporaryValue, randomIndex ;
        // While there remains elements to shuffle...
        while (0 !== currentIndex) {
            // Pick a remaining element...
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex -= 1;
            // And swap it with the current element.
            temporaryValue = array[currentIndex];
            array[currentIndex] = array[randomIndex];
            array[randomIndex] = temporaryValue;
        }
        return array;
    };

    // Extend a parent class prototype to a child
    //
    // 1. Parent class: implement as usual exposing prototype methods/data
    // 2. Child class:
    //
    //    a. declare 'parentPrototype' in module var list
    //
    //    b. In first line of child constructor, instantiate the parent class
    //
    //          // Super instantiation
    //          <parent-class>.call(this, <parent constructor args, if any>);
    //
    //          Example:
    //          Offer.call(this, Offer.prototype.types.TEXT);
    //
    //    c. Immediately after the child constructor add call to
    //       utilities.extends() to extend the parent's prototype to the child
    //       prototype and keep a handy reference to the parent's prototype.
    //
    //          // Inherit prototype methods from super class
    //          parentPrototype = utilities.extends(<parent>, <child>);
    //
    //          Example:
    //          parentPrototype = utilities.extends(Offer, TextOffer);
    //
    // Full Example: See Offer.js and TextOffer.js
    //
    Utilities.prototype.extends = function (parent, child) {
        // Set subclass prototype to superclass prototype
        child.prototype = Object.create(parent.prototype);
        // Reset child constructor back to original constructor
        child.prototype.constructor = child;
        // Return the parent for to enable parent method calls
        return parent.prototype;
    };

    // Get type of object for parameter
    Utilities.prototype.getType = function (entity) {
        return Object.prototype.toString.call(entity).slice(8, -1);
    };

    // Determine if this app is running from the Merchant website
    Utilities.prototype.isMerchantWebsite = function () {
        return window.location.pathname === merchantWebsitePathname;
    };

    // Determine if a CSS file is linked via the head tag
    Utilities.prototype.isCssLinked = function (relativePath) {

        // Get links
        var head = document.getElementsByTagName('head')[0],
        headLinks = head.getElementsByTagName('link'),
        linked = false;

        // Check for relative path on CSS links
        for (var i=0; i<headLinks.length; i++) {
            if (headLinks[i].type === 'text/css' &&
                headLinks[i].rel === 'stylesheet' &&
                headLinks[i].href === relativePath) {
                linked = true;
                break;
            }
        }

        // Return true if CSS link found
        return linked;
    };

    // Link an additional CSS file
    Utilities.prototype.linkCss = function (relativePath) {
        if (!this.isCssLinked(relativePath)) {
            var link = document.createElement('link');
            link.type = 'text/css';
            link.rel = 'stylesheet';
            link.href = relativePath;
            document.getElementsByTagName('head')[0].appendChild(link);
        }
    };

    // Return Constructor
    return Utilities.prototype.constructor;
});

